import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Dumbbell, Target, Activity, Play, Heart } from "lucide-react";
import { exerciseApi, youtubeApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ExerciseDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  const { data: exercise, isLoading: isLoadingExercise } = useQuery({
    queryKey: ["exercise", id],
    queryFn: () => exerciseApi.getExerciseById(id!),
    enabled: !!id,
  });

  const { data: similarByTarget } = useQuery({
    queryKey: ["similar-target", exercise?.target],
    queryFn: () => exerciseApi.getExercisesByTarget(exercise!.target),
    enabled: !!exercise?.target,
  });

  const { data: similarByEquipment } = useQuery({
    queryKey: ["similar-equipment", exercise?.equipment],
    queryFn: () => exerciseApi.getExercisesByEquipment(exercise!.equipment),
    enabled: !!exercise?.equipment,
  });

  if (isLoadingExercise) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <Skeleton className="h-8 w-32 mb-8" />
          <div className="grid md:grid-cols-2 gap-8">
            <Skeleton className="aspect-square rounded-xl" />
            <div className="space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-1/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!exercise) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl font-bold mb-4">Exercise Not Found</h1>
          <p className="text-muted-foreground mb-6">
            The exercise you're looking for doesn't exist.
          </p>
          <Link to="/exercises">
            <Button>Browse Exercises</Button>
          </Link>
        </div>
      </div>
    );
  }

  const similarExercises = [
    ...(similarByTarget || []),
    ...(similarByEquipment || []),
  ]
    .filter((e) => e.id !== exercise.id)
    .slice(0, 6);

  return (
    <div className="min-h-screen pt-24 pb-12 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <Link to="/exercises" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to Exercises
        </Link>

        {/* Main Content */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Exercise GIF */}
          <Card className="overflow-hidden">
            <img
              src={exercise.gifUrl}
              alt={exercise.name}
              className="w-full aspect-square object-cover"
            />
          </Card>

          {/* Exercise Info */}
          <div>
            <h1 className="text-3xl font-bold capitalize mb-4">{exercise.name}</h1>
            
            <div className="flex flex-wrap gap-2 mb-6">
              <Badge className="bg-amber-500/10 text-amber-500 hover:bg-amber-500/20">
                <Dumbbell className="w-3 h-3 mr-1" />
                {exercise.bodyPart}
              </Badge>
              <Badge variant="secondary">
                <Target className="w-3 h-3 mr-1" />
                {exercise.target}
              </Badge>
              <Badge variant="outline">
                <Activity className="w-3 h-3 mr-1" />
                {exercise.equipment}
              </Badge>
            </div>

            {/* Secondary Muscles */}
            {exercise.secondaryMuscles && exercise.secondaryMuscles.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold mb-2">Secondary Muscles</h3>
                <div className="flex flex-wrap gap-2">
                  {exercise.secondaryMuscles.map((muscle) => (
                    <Badge key={muscle} variant="outline" className="text-xs">
                      {muscle}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Instructions */}
            {exercise.instructions && exercise.instructions.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold mb-3">Instructions</h3>
                <ol className="space-y-2">
                  {exercise.instructions.map((instruction, idx) => (
                    <li key={idx} className="flex gap-3 text-sm text-muted-foreground">
                      <span className="font-medium text-amber-500 min-w-[1.5rem]">
                        {idx + 1}.
                      </span>
                      {instruction}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4">
              {user && (
                <Button variant="outline" className="gap-2">
                  <Heart className="w-4 h-4" />
                  Add to Favorites
                </Button>
              )}
              <Button variant="outline" className="gap-2" asChild>
                <a
                  href={`https://www.youtube.com/results?search_query=${encodeURIComponent(
                    exercise.name + " exercise"
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Play className="w-4 h-4" />
                  Watch on YouTube
                </a>
              </Button>
            </div>
          </div>
        </div>

        {/* Similar Exercises */}
        {similarExercises.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Similar Exercises</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {similarExercises.map((similar) => (
                <Link key={similar.id} to={`/exercises/${similar.id}`}>
                  <Card className="overflow-hidden hover:border-amber-500/50 transition-colors">
                    <img
                      src={similar.gifUrl}
                      alt={similar.name}
                      className="w-full aspect-square object-cover"
                      loading="lazy"
                    />
                    <div className="p-3">
                      <p className="font-medium text-sm capitalize line-clamp-2">
                        {similar.name}
                      </p>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
